import { CSSProperties, FC } from 'react';
import { Avatar, Col, Row, Typography } from 'antd';
import { parseURL } from 'ufo';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { AvatarSkeleton, ButtonSkeleton } from '@/shared/components/skeletons';
import { dayjs, ellipsisStyling } from '@/utilities';

export interface UnfurledLinkPreviewProps {
    author?: string;
    date?: string;
    density?: 'regular' | 'compact';
    description?: string;
    image?: string;
    isLoading?: boolean;
    logo?: string;
    onClick?: () => void;
    publisher?: string;
    style?: CSSProperties;
    title?: string;
    url?: string;
}

const UnfurledLinkPreview: FC<UnfurledLinkPreviewProps> = ({
    author,
    date,
    density = 'regular',
    description,
    image,
    isLoading,
    logo,
    onClick,
    publisher,
    style,
    title,
    url,
}) => {
    const { token } = useAntDesignTheme();
    const compact = density === 'compact';
    const parsedUrl = url ? parseURL(url) : undefined;
    const host = parsedUrl?.host ?? url;
    const displayTitle = title || host || 'Untitled Link';
    const displayDescription = description?.trim() || undefined;
    const displayDate = date ? dayjs(date).format('LL') : undefined;
    const sourceLabel = [publisher || host, author, displayDate].filter(Boolean).join(' · ');
    const cardHeight = compact ? '7rem' : '8.25rem';
    const contentPadding = compact ? '2.5rem 0.5rem' : '2.5rem 0.75rem';
    const descriptionFontSize = compact ? token.fontSizeSM : token.fontSize;
    const loadingLine = (width: string, height = compact ? '0.75rem' : '0.875rem') => (
        <ButtonSkeleton
            block
            style={{
                display: 'block',
                height,
                minWidth: 0,
                width,
            }}
        />
    );

    if (isLoading) {
        return (
            <Row
                align='middle'
                wrap={false}
                style={{
                    border: `1px solid ${token.colorBorder}`,
                    borderRadius: token.borderRadiusLG,
                    background: token.colorBgContainer,
                    display: 'flex',
                    gap: 0,
                    height: cardHeight,
                    maxHeight: cardHeight,
                    minHeight: cardHeight,
                    overflow: 'hidden',
                    ...style,
                }}
            >
                <Col
                    flex='auto'
                    style={{
                        minWidth: 0,
                        overflow: 'hidden',
                        padding: contentPadding,
                    }}
                >
                    <div style={{ marginBottom: compact ? '0.375rem' : '0.5rem', width: '36%' }}>
                        {loadingLine('100%', compact ? '0.625rem' : '0.75rem')}
                    </div>
                    <div style={{ marginBottom: compact ? '0.5rem' : '0.625rem', width: '58%' }}>
                        {loadingLine('100%', compact ? '1rem' : '1.125rem')}
                    </div>
                    <div style={{ marginBottom: '0.375rem', width: '92%' }}>{loadingLine('100%')}</div>
                    <div style={{ marginBottom: compact ? '0.5rem' : '0.625rem', width: '74%' }}>
                        {loadingLine('100%')}
                    </div>
                    <Row align='middle' wrap={false} gutter={8}>
                        <Col flex='none'>
                            <AvatarSkeleton
                                size={16}
                                style={{
                                    borderRadius: token.borderRadiusSM,
                                    display: 'block',
                                }}
                            />
                        </Col>
                        <Col flex='auto' style={{ minWidth: 0 }}>
                            {loadingLine('68%', compact ? '0.625rem' : '0.75rem')}
                        </Col>
                    </Row>
                </Col>
                <Col
                    flex='0 0 32%'
                    style={{
                        alignSelf: 'stretch',
                        background: token.colorFillQuaternary,
                        height: cardHeight,
                        maxWidth: compact ? 132 : 320,
                        minWidth: compact ? 96 : 144,
                    }}
                />
            </Row>
        );
    }

    return (
        <Row
            align='middle'
            wrap={false}
            data-memo-toc-ignore='true'
            draggable
            style={{
                border: `1px solid ${token.colorBorder}`,
                borderRadius: token.borderRadiusLG,
                background: token.colorBgContainer,
                display: 'flex',
                gap: 0,
                cursor: onClick || url ? 'pointer' : 'default',
                height: cardHeight,
                maxHeight: cardHeight,
                minHeight: cardHeight,
                overflow: 'hidden',
                ...style,
            }}
            onClick={() => {
                if (onClick) {
                    onClick();
                    return;
                }

                if (url) {
                    window.open(url, '_blank', 'noopener,noreferrer');
                }
            }}
        >
            <Col
                flex='auto'
                style={{
                    minWidth: 0,
                    overflow: 'hidden',
                    padding: contentPadding,
                }}
            >
                {sourceLabel && (
                    <small
                        style={{
                            ...ellipsisStyling,
                            color: token.colorTextTertiary,
                            display: 'block',
                            marginBottom: compact ? '0.25rem' : '0.375rem',
                        }}
                    >
                        {sourceLabel}
                    </small>
                )}
                <Typography.Text
                    strong
                    ellipsis
                    style={{
                        color: token.colorText,
                        display: 'block',
                        fontSize: compact ? token.fontSize : token.fontSizeLG,
                        lineHeight: 1.25,
                        margin: 0,
                        marginBottom: displayDescription ? '0.25rem' : compact ? '0.5rem' : '0.625rem',
                    }}
                >
                    {displayTitle}
                </Typography.Text>
                {displayDescription && (
                    <Typography.Paragraph
                        ellipsis={{ rows: 2, expandable: false }}
                        style={{
                            color: token.colorTextSecondary,
                            display: '-webkit-box',
                            fontSize: descriptionFontSize,
                            height: descriptionFontSize * 1.35 * 2,
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 2,
                            lineHeight: 1.35,
                            margin: 0,
                            marginBottom: compact ? '0.5rem' : '0.625rem',
                            overflow: 'hidden',
                        }}
                    >
                        {displayDescription}
                    </Typography.Paragraph>
                )}
                <Row align='middle' wrap={false} gutter={8}>
                    <Col flex='none'>
                        <Avatar
                            shape='square'
                            size={16}
                            src={logo}
                            style={{
                                border: `1px solid ${token.colorBorder}`,
                                background: token.colorBgBase,
                                color: token.colorTextTertiary,
                                fontSize: 10,
                            }}
                        >
                            {(publisher || host || 'L').slice(0, 1).toUpperCase()}
                        </Avatar>
                    </Col>
                    <Col flex='auto' style={{ minWidth: 0 }}>
                        <Typography.Text
                            ellipsis
                            style={{
                                color: token.colorTextTertiary,
                                display: 'block',
                                fontSize: compact ? token.fontSizeSM : token.fontSize,
                            }}
                        >
                            {url}
                        </Typography.Text>
                    </Col>
                </Row>
            </Col>
            {image && (
                <Col
                    flex={compact ? '0 0 32%' : '0 0 32%'}
                    style={{
                        alignSelf: 'stretch',
                        height: cardHeight,
                        maxWidth: compact ? 132 : 320,
                        minWidth: compact ? 96 : 144,
                        overflow: 'hidden',
                        borderLeft: `1px solid ${token.colorBorder}`,
                    }}
                >
                    <img
                        src={image}
                        alt={displayTitle}
                        style={{
                            display: 'block',
                            width: '100%',
                            height: '100%',
                            maxHeight: cardHeight,
                            objectFit: 'cover',
                        }}
                    />
                </Col>
            )}
        </Row>
    );
};

export default UnfurledLinkPreview;
